// @flow

import {
  type IncomingPayment,
  type IncomingPaymentStatus,
  type OpenedIncomingPayment,
  getStatus,
  getCountry,
  getSafePaymentType,
  getSafePaymentTypeCode, getSafeAllowedPaymentTypes,
} from '@tectonics/prs/incoming-payment';

import { type Feature } from '@tectonics/prs/feature';
import { type PaymentMethod } from '@tectonics/prs/payment-method';
import { type Country } from '@tectonics/prs/country';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';

import { type PaymentSpecGenerator } from './types';

type GenerateSpecArgs = {|
  +incomingPayment: IncomingPayment,
  +generator: PaymentSpecGenerator,
  +localTenancy: string,
  +lookAndFeelCode: string,
|};

export function generatePaymentSpec({ incomingPayment, generator, localTenancy, lookAndFeelCode }: GenerateSpecArgs): PaymentSpec {
  const status = getStatus(incomingPayment);
  const country = getCountry(incomingPayment);

  return {
    country,
    features: generator.features({ status, country, incomingPayment, localTenancy, lookAndFeelCode }),
    paymentMethods: generatePaymentMethods({ status, country, incomingPayment, generator, localTenancy, lookAndFeelCode }),
  };
}

type GeneratePaymentMethodsArgs = {|
  +status: IncomingPaymentStatus,
  +country: Country,
  +incomingPayment: IncomingPayment,
  +generator: PaymentSpecGenerator,
  +localTenancy: string,
  +lookAndFeelCode: string,
|};

function generatePaymentMethods({
  incomingPayment,
  status,
  country,
  generator,
  localTenancy,
  lookAndFeelCode,
}: GeneratePaymentMethodsArgs): ImmutableArray<PaymentMethod> {
  switch (incomingPayment.status) {
    case 'OP': {
      const allowedPaymentTypes = getSafeAllowedPaymentTypes(incomingPayment);
      return generateAllowedPaymentTypesBasedPaymentMethods({
        allowedPaymentTypes,
        country,
        incomingPayment,
        generator,
        localTenancy,
        lookAndFeelCode,
      });
    }

    case 'CO':
    case 'PE': {
      const paymentType = getSafePaymentType(incomingPayment);
      return [
        {
          code: paymentType,
          features: generatePaymentTypeBasedFeatures({
            paymentType,
            country,
            status,
            incomingPayment,
            generator,
            localTenancy,
            lookAndFeelCode,
          }),
        },
      ];
    }

    default:
      return [];
  }
}

type GeneratePaymentOptionsBasedPaymentMethodsArgs = {|
  +allowedPaymentTypes: ImmutableArray<string>,
  +country: Country,
  +incomingPayment: OpenedIncomingPayment,
  +generator: PaymentSpecGenerator,
  +localTenancy: string,
  +lookAndFeelCode: string,
|};

function generateAllowedPaymentTypesBasedPaymentMethods({
  allowedPaymentTypes,
  country,
  incomingPayment,
  generator,
  localTenancy,
  lookAndFeelCode,
}: GeneratePaymentOptionsBasedPaymentMethodsArgs): ImmutableArray<PaymentMethod> {
  const paymentMethods: PaymentMethod[] = [];

  allowedPaymentTypes
    .filter(allowedPaymentType => allowedPaymentType in generator)
    .forEach(allowedPaymentType => {
      const features = generator[allowedPaymentType]({ country, status: 'OP', incomingPayment, localTenancy, lookAndFeelCode });
      if (features) {
        paymentMethods.push({
          code: allowedPaymentType,
          features,
        });
      }
    });

  return paymentMethods;
}

type GeneratePaymentTypeBasedFeaturesArgs = {|
  +status: IncomingPaymentStatus,
  +paymentType: string,
  +country: Country,
  +incomingPayment: IncomingPayment,
  +generator: PaymentSpecGenerator,
  +localTenancy: string,
  +lookAndFeelCode: string,
|};

function generatePaymentTypeBasedFeatures({
  status,
  paymentType,
  country,
  incomingPayment,
  generator,
  localTenancy,
  lookAndFeelCode,
}: GeneratePaymentTypeBasedFeaturesArgs): ImmutableArray<Feature> {
  if (paymentType in generator) {
    const paymentTypeCode = getSafePaymentTypeCode(incomingPayment);
    const features = generator[paymentType]({ country, status, incomingPayment, paymentTypeCode, localTenancy, lookAndFeelCode });
    if (!features) throw new Error(`Payment Spec Generator - There are no features for "${paymentType}"`);
    return features;
  }

  return [];
}
